import React from "react";
import { Map } from "immutable";
import getSlug from "speakingurl";
import { Input, Select, SelectOption, Switch, Text, Tooltip } from "@showitinc/showit-wc";
import { useBlockStates } from "../hooks/useBlockStates";
import BlockSelectInput from "./BlockSelectInput";
import PageSelectInput from "./PageSelectInput";
import BlockStateSelectInput from "./BlockStateSelectInput";
import AppConstants from "../constants/AppConstants";
import { SOCIAL_LINKS_MAP } from "../stores/SiteSettingsStore";
import When from "./utils/When";
import { initFormatter } from "../utils/PropertyFormatters";
import HelpTooltip from "../constants/HelpTooltips";
import slugify from "../utils/SlugifyUtils";
import { isBlockTargetValid, isPageTargetValid } from "../utils/LinkUtils";
import { SUBMIT_FORM_LINK } from "../constants/FormFieldConstants.mjs";
import { FORMS_FLAG } from "../constants/FeatureFlags.mjs";
import useV2Feature from "../hooks/useV2Feature";

const {
  LINK_SOCIAL_EMAIL,
  LINK_SOCIAL_FACEBOOK,
  LINK_SOCIAL_LINKED_IN,
  LINK_SOCIAL_PINTEREST,
  LINK_SOCIAL_TWITTER,
  LINK_STATE_NEXT,
  LINK_STATE_PREVIOUS,
  LINK_TYPE_BLOCK,
  LINK_TYPE_EMAIL,
  LINK_TYPE_NONE,
  LINK_TYPE_PAGE,
  LINK_TYPE_PAGE_TOP,
  LINK_TYPE_PHONE,
  LINK_TYPE_SHARE,
  LINK_TYPE_SMS,
  LINK_TYPE_SOCIAL,
  LINK_TYPE_URL,
  LINK_TYPE_WP_POST,
  UPDATE_ACTION_DELETE,
  UPDATE_ACTION_SET,
} = AppConstants;

const LINK = "link";
const LINK_TYPE = "type";
const LINK_TARGET = "target";
const LINK_TARGET_BLOCK = "block";
const LINK_TARGET_STATE = "state";
const LINK_NEW_WINDOW = "newWindow";
const LINK_NO_SCROLL = "noScroll";
const LINK_NO_REVERSE = "noReverse";
const LINK_NO_LOOP = "noLoop";
const LINK_SUB_FORM = "subForm";
const LINK_NO_FOLLOW = "nf";

const MODE_ELEMENT = "element";
const MODE_INLINE = "inline";

const canOpenNewWindow = [LINK_TYPE_PAGE, LINK_TYPE_URL];
const canSubmitForm = [LINK_TYPE_PAGE, LINK_TYPE_BLOCK, LINK_TYPE_URL];
/** Type menu value for a `subForm` link. Never saved: the link keeps its after-submit `type`. */
const LINK_TYPE_SUBMIT_FORM = "submitForm";

/** @type {{ [key: string]: (value: string) => boolean }} */
const TARGET_VALIDATORS = {
  [LINK_TYPE_PAGE]: isPageTargetValid,
  [LINK_TYPE_BLOCK]: isBlockTargetValid,
};

/**
 * Checks if the target is valid (or exists) for the type
 * @param {string | undefined} target
 * @param {keyof typeof TARGET_VALIDATORS} type
 * @returns {boolean}
 */
const targetIsValid = (target, type) =>
  type in TARGET_VALIDATORS ? TARGET_VALIDATORS[type](target) : false;

const TargetInput = ({ target, type, isLocked, onPropUpdate }) => {
  const { onChange } = initFormatter(onPropUpdate);

  const propName = `${LINK}.${LINK_TARGET}`;

  switch (type) {
    case LINK_TYPE_PAGE:
      return (
        <PageSelectInput
          data-testid={`link-page-select`}
          disabled={isLocked}
          label="Page"
          onSISelect={onChange(propName, target)}
          reserveContext
          shift="left"
          value={target}
        />
      );

    case LINK_TYPE_BLOCK:
      return (
        <BlockSelectInput
          isLocked={isLocked}
          label="Canvas"
          onChange={onPropUpdate}
          propName={propName}
          value={target}
        />
      );
    case LINK_TYPE_SOCIAL:
      return (
        <Select
          disabled={isLocked}
          label="Link To"
          onSISelect={onChange(propName, target)}
          reserveContext
          shift="left"
          value={target}
        >
          <SelectOption value="">Select Platform</SelectOption>
          {Object.entries(SOCIAL_LINKS_MAP).map(([key, socialPlatform]) => (
            <SelectOption value={key} key={key}>
              {socialPlatform}
            </SelectOption>
          ))}
        </Select>
      );
    case LINK_TYPE_URL:
      return (
        <Input
          disabled={isLocked}
          label="URL"
          onSIBeforeChange={onChange(propName, target)}
          onSIInput={onChange(propName, target, false)}
          reserveContext
          placeholder="https://"
          value={target}
        />
      );
    case LINK_TYPE_EMAIL:
      return (
        <Input
          disabled={isLocked}
          label="Email"
          placeholder="Enter email address"
          onSIBeforeChange={onChange(propName, target)}
          onSIInput={onChange(propName, target, false)}
          reserveContext
          value={target}
        />
      );

    case LINK_TYPE_SMS:
      return (
        <Input
          disabled={isLocked}
          label="Mobile #"
          placeholder="Enter mobile number"
          onSIBeforeChange={onChange(propName, target)}
          onSIInput={onChange(propName, target, false)}
          reserveContext
          value={target}
        />
      );
    case LINK_TYPE_PHONE:
      return (
        <Input
          disabled={isLocked}
          label="Phone #"
          placeholder="Enter phone number"
          onSIBeforeChange={onChange(propName, target)}
          onSIInput={onChange(propName, target, false)}
          reserveContext
          value={target}
        />
      );

    case LINK_TYPE_SHARE:
      return (
        <Select
          disabled={isLocked}
          label="Share Page To"
          onSISelect={onChange(propName, target)}
          reserveContext
          shift="left"
          value={target}
        >
          <SelectOption value=""> Select Platform </SelectOption>
          <SelectOption value={LINK_SOCIAL_FACEBOOK}>Facebook</SelectOption>
          <SelectOption value={LINK_SOCIAL_PINTEREST}>Pinterest</SelectOption>
          <SelectOption value={LINK_SOCIAL_TWITTER}>X (Twitter)</SelectOption>
          <SelectOption value={LINK_SOCIAL_LINKED_IN}>LinkedIn</SelectOption>
          <SelectOption value={LINK_SOCIAL_EMAIL}>Email</SelectOption>
        </Select>
      );
    default:
      return null;
  }
};

const LinkProperties = ({
  link = Map(),
  mode = MODE_ELEMENT,
  onPropUpdate,
  isLocked,
  isWP,
  isGroup,
}) => {
  const isFormsEnabled = useV2Feature(FORMS_FLAG);

  const _onPropChange = (propInfo) => {
    if (onPropUpdate) {
      onPropUpdate(propInfo);
      // Auto-toggle off loop canvas views if state is not NEXT or PREVIOUS
      if (
        propInfo.name === `${LINK}.${LINK_TARGET_STATE}` &&
        ![LINK_STATE_NEXT, LINK_STATE_PREVIOUS].includes(propInfo.value)
      ) {
        onPropUpdate({
          ...propInfo,
          name: `${LINK}.${LINK_NO_LOOP}`,
          value: 1,
        });
      }
    }
  };

  const _onCanvasSlugChange = (propInfo) => {
    const { value } = propInfo;
    _onPropChange({ ...propInfo, value: value ? slugify(value) : value });
  };

  const _onTypeChange = (propInfo) => {
    const { value } = propInfo;

    if (value === LINK_TYPE_SUBMIT_FORM) {
      onPropUpdate({
        ...propInfo,
        action: UPDATE_ACTION_SET,
        name: LINK,
        value: { ...SUBMIT_FORM_LINK },
      });
      return;
    }

    onPropUpdate({
      ...propInfo,
      action: value && value !== LINK_TYPE_NONE ? UPDATE_ACTION_SET : UPDATE_ACTION_DELETE,
      name: "link",
      value: { type: value, target: null },
    });
  };

  /**
   * Swaps where a submit goes afterward, keeping it a form submit.
   * @param {Object} propInfo - The property information; `value` is the after-submit link type.
   * @returns {void}
   */
  const _onAfterSubmitChange = (propInfo) => {
    const { value } = propInfo;

    onPropUpdate({
      ...propInfo,
      action: UPDATE_ACTION_SET,
      name: LINK,
      value:
        value === LINK_TYPE_BLOCK
          ? { ...SUBMIT_FORM_LINK }
          : { type: value, target: null, subForm: 1 },
    });
  };

  const _onCheckChange = (propInfo) => {
    const { value } = propInfo;
    _onPropChange({ ...propInfo, value: value ? 1 : null });
  };

  const _onNegativeChange = (propInfo) => {
    const { value } = propInfo;
    _onPropChange({ ...propInfo, value: value ? null : 1 });
  };

  /**
   * Clears the state when the target (canvas) has no views or has different views.
   * I.e. if the user selects a different canvas, the dropdown should be cleared.
   * @param {Object} propInfo - The property information
   * @returns {void}
   */
  const _onTargetChange = (propInfo) => {
    if (onPropUpdate) {
      if (
        propInfo.name === `${LINK}.${LINK_TARGET}` &&
        propInfo.value !== target &&
        state != null &&
        state !== ""
      ) {
        onPropUpdate({
          name: `${LINK}.${LINK_TARGET_STATE}`,
          value: null,
          action: UPDATE_ACTION_DELETE,
        });
      }
      onPropUpdate(propInfo);
    }
  };

  const type = link.get(LINK_TYPE, LINK_TYPE_NONE);
  const target = link.get(LINK_TARGET, null);
  const newWin = !(link.get(LINK_NEW_WINDOW, false) === false);
  const block = link.get(LINK_TARGET_BLOCK, "");
  const state = link.get(LINK_TARGET_STATE, null);
  const noScroll = link.get(LINK_NO_SCROLL, false);
  const noReverse = link.get(LINK_NO_REVERSE, false);
  const noLoop = link.get(LINK_NO_LOOP, false);
  const subForm = link.get(LINK_SUB_FORM, false);
  const noFollow = !(link.get(LINK_NO_FOLLOW, false) === false);

  const isElement = mode === MODE_ELEMENT;
  // Without native forms, a submit stays the legacy switch on a Canvas, Page or URL link.
  const hasSubmitType = isElement && isFormsEnabled;
  const isSubmit = hasSubmitType && Boolean(subForm);
  const typeMenuValue = isSubmit ? LINK_TYPE_SUBMIT_FORM : type;
  const { hasNoViews: blockHasNoViews } = useBlockStates(
    type === LINK_TYPE_BLOCK ? target : null
  );

  const { onChange: onTypeChange } = initFormatter(_onTypeChange);
  const { onChange: onAfterSubmitChange } = initFormatter(_onAfterSubmitChange);
  const { onChange: onSlugChange } = initFormatter(_onCanvasSlugChange);
  const { onChange: onCheckChange } = initFormatter(_onCheckChange);
  const { onChange: onNegativeChange } = initFormatter(_onNegativeChange);

  return (
    <>
      <Select
        data-testid="link-type-select"
        disabled={isLocked}
        label="Type"
        onSISelect={onTypeChange(`${LINK}.${LINK_TYPE}`, typeMenuValue)}
        reserveContext
        shift="left"
        value={typeMenuValue}
      >
        <SelectOption data-testid="link-type-none-option" value={LINK_TYPE_NONE}>
          None
        </SelectOption>
        <SelectOption data-testid="link-type-page-option" value={LINK_TYPE_PAGE}>
          Page
        </SelectOption>
        <SelectOption data-testid="link-type-block-option" value={LINK_TYPE_BLOCK}>
          Canvas
        </SelectOption>
        <SelectOption data-testid="link-type-page-top-option" value={LINK_TYPE_PAGE_TOP}>
          Top Of Page
        </SelectOption>
        <SelectOption data-testid="link-type-social-option" value={LINK_TYPE_SOCIAL}>
          Social Link
        </SelectOption>
        <SelectOption data-testid="link-type-url-option" value={LINK_TYPE_URL}>
          URL
        </SelectOption>
        <SelectOption data-testid="link-type-share-option" value={LINK_TYPE_SHARE}>
          Share on Social
        </SelectOption>
        <SelectOption data-testid="link-type-email-option" value={LINK_TYPE_EMAIL}>
          Email
        </SelectOption>
        <SelectOption data-testid="link-type-sms-option" value={LINK_TYPE_SMS}>
          SMS
        </SelectOption>
        <SelectOption data-testid="link-type-phone-option" value={LINK_TYPE_PHONE}>
          Phone
        </SelectOption>
        {hasSubmitType && (
          <SelectOption data-testid="link-type-submit-form-option" value={LINK_TYPE_SUBMIT_FORM}>
            Submit Form
          </SelectOption>
        )}
        {(isWP ? !isGroup : false) && (
          <SelectOption value={LINK_TYPE_WP_POST}>WordPress Post</SelectOption>
        )}
      </Select>

      {isSubmit && (
        <Select
          data-testid="link-after-submit-select"
          disabled={isLocked}
          label="After Submit"
          onSISelect={onAfterSubmitChange(`${LINK}.${LINK_TYPE}`, type)}
          reserveContext
          shift="left"
          value={type}
        >
          <SelectOption data-testid="link-after-submit-block-option" value={LINK_TYPE_BLOCK}>
            Canvas
          </SelectOption>
          <SelectOption data-testid="link-after-submit-page-option" value={LINK_TYPE_PAGE}>
            Page
          </SelectOption>
          <SelectOption data-testid="link-after-submit-url-option" value={LINK_TYPE_URL}>
            URL
          </SelectOption>
        </Select>
      )}
      <TargetInput target={target} type={type} isLocked={isLocked} onPropUpdate={_onTargetChange} />
      {/* Block Ops */}
      <When it={target}>
        {type === LINK_TYPE_PAGE && targetIsValid(target, type) && (
          <>
            <Input
              data-testid="link-canvas-slug-input"
              disabled={isLocked}
              label="Canvas Slug"
              onSIBeforeChange={onSlugChange(`${LINK}.${LINK_TARGET_BLOCK}`, block)}
              onSIInput={onSlugChange(`${LINK}.${LINK_TARGET_BLOCK}`, block, false)}
              reserveContext
              shift="left"
              value={block}
            />
            <BlockStateSelectInput
              key={`block-state-select-${target}-${block}`}
              propName={`${LINK}.${LINK_TARGET_STATE}`}
              disabled={isLocked}
              enableNextPrevious={false}
              onChange={_onPropChange}
              value={state}
              blockSlug={block}
              pageSlug={target}
            />
          </>
        )}
        {type === LINK_TYPE_BLOCK && targetIsValid(target, type) && (
          <>
            <BlockStateSelectInput
              key={`block-state-select-${target}`}
              propName={`${LINK}.${LINK_TARGET_STATE}`}
              disabled={isLocked}
              value={state}
              onChange={_onPropChange}
              blockSlug={target}
            />
            {isElement && state != null && state !== "" && !blockHasNoViews && (
              <>
                <Switch
                  checked={!noScroll}
                  disabled={isLocked}
                  label="Scroll to Canvas"
                  onSIChange={onNegativeChange(`${LINK}.${LINK_NO_SCROLL}`, !noScroll)}
                  reserveContext
                />
                <Switch
                  checked={!noReverse}
                  disabled={isLocked}
                  label="Reverse Transition"
                  onSIChange={onNegativeChange(`${LINK}.${LINK_NO_REVERSE}`, !noReverse)}
                  reserveContext
                />
                {[LINK_STATE_NEXT, LINK_STATE_PREVIOUS].includes(state) && (
                  <Switch
                    checked={!noLoop}
                    disabled={isLocked}
                    label="Loop Canvas Views"
                    onSIChange={onNegativeChange(`${LINK}.${LINK_NO_LOOP}`, !noLoop)}
                    reserveContext
                  />
                )}
              </>
            )}
          </>
        )}
      </When>
      {
        /* New Window Checkbox */
        canOpenNewWindow.includes(type) && (
          <Switch
            checked={newWin}
            disabled={isLocked}
            label="Open new window"
            onSIChange={onCheckChange(`${LINK}.${LINK_NEW_WINDOW}`, newWin)}
            reserveContext
          />
        )
      }
      {
        /* Do Not Follow Checkbox */
        [LINK_TYPE_PAGE, LINK_TYPE_URL].includes(type) && (
          <Tooltip position="bottom-left">
            <Switch
              checked={noFollow}
              disabled={isLocked}
              label="Do not follow"
              onSIChange={onCheckChange(`${LINK}.${LINK_NO_FOLLOW}`, noFollow)}
            />
            <Text slot="content">{HelpTooltip["seo-nofollow"]?.t}</Text>
          </Tooltip>
        )
      }
      {
        /* Submit Form Checkbox */
        isElement && !isFormsEnabled && canSubmitForm.includes(type) && target && (
          <Switch
            checked={subForm}
            disabled={isLocked}
            label="Submit Form"
            onSIChange={onCheckChange(`${LINK}.${LINK_SUB_FORM}`, subForm)}
          />
        )
      }
    </>
  );
};

export default LinkProperties;
export { MODE_ELEMENT, MODE_INLINE };
